import { useMemo } from 'react';

// Lilac comic speech bubble with a brown outline and a brownish number inside,
// drawn as a single SVG path so the stroke wraps the body and the tail
// continuously (classic comic-book look). Hidden when there is nothing to show.
//
//   count - number to display (0 hides the bubble)
//   size  - 'sm' for the nav, 'md' for the page header
//   tail  - 'down' (points down, sits on top of a target) | 'up' (points up, sits below a target)
//   className - positioning helpers on the wrapping span
const LILA = '#c9a7eb';
const BROWN = '#6e4327';
const TEXT = '#3f2510';

const SIZES = {
    sm: { w: 26, bodyH: 20, r: 9, tailH: 7, tHalf: 4, font: 11 },
    md: { w: 50, bodyH: 38, r: 13, tailH: 11, tHalf: 7, font: 18 },
};

export function ComicBubble({ count, size = 'sm', tail = 'down', className = '' }) {
    const cfg = SIZES[size] || SIZES.sm;
    const { path, W, H, textOffset } = useMemo(() => {
        const P = 2; // viewBox padding so the 3px stroke is never clipped
        const W = cfg.w + P * 2;
        const H = cfg.bodyH + cfg.tailH + P * 2;
        const x1 = P, x2 = P + cfg.w;
        const y1 = tail === 'up' ? P + cfg.tailH : P;
        const y2 = y1 + cfg.bodyH;
        const cx = W / 2;
        const { r, tailH, tHalf } = cfg;
        // Single closed path: rounded body with the tail cut into one edge, so
        // the brown stroke runs around the whole silhouette without a seam.
        const d = tail === 'up'
            ? [
                `M ${x1 + r} ${y1}`,
                `L ${cx - tHalf} ${y1}`,
                `L ${cx} ${y1 - tailH}`,
                `L ${cx + tHalf} ${y1}`,
                `H ${x2 - r}`,
                `Q ${x2} ${y1} ${x2} ${y1 + r}`,
                `V ${y2 - r}`,
                `Q ${x2} ${y2} ${x2 - r} ${y2}`,
                `H ${x1 + r}`,
                `Q ${x1} ${y2} ${x1} ${y2 - r}`,
                `V ${y1 + r}`,
                `Q ${x1} ${y1} ${x1 + r} ${y1}`,
                'Z',
            ].join(' ')
            : [
                `M ${x1 + r} ${y1}`,
                `H ${x2 - r}`,
                `Q ${x2} ${y1} ${x2} ${y1 + r}`,
                `V ${y2 - r}`,
                `Q ${x2} ${y2} ${x2 - r} ${y2}`,
                `L ${cx + tHalf} ${y2}`,
                `L ${cx} ${y2 + tailH}`,
                `L ${cx - tHalf} ${y2}`,
                `H ${x1 + r}`,
                `Q ${x1} ${y2} ${x1} ${y2 - r}`,
                `V ${y1 + r}`,
                `Q ${x1} ${y1} ${x1 + r} ${y1}`,
                'Z',
            ].join(' ');
        // The wrapper is centered on the whole viewBox (body + tail), so nudge
        // the number back to the middle of the body.
        const textOffset = tail === 'up' ? tailH / 2 : -tailH / 2;
        return { path: d, W, H, textOffset };
    }, [cfg, tail]);

    if (!count) return null;
    const label = count > 99 ? '99+' : String(count);

    return (
        <span className={`relative inline-flex items-center justify-center ${className}`} style={{ width: W, height: H }}>
            <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 block" aria-hidden="true">
                <path d={path} fill={LILA} stroke={BROWN} strokeWidth={3} strokeLinejoin="round" />
            </svg>
            <span className="relative font-bold leading-none" style={{ color: TEXT, fontSize: cfg.font, transform: `translateY(${textOffset}px)` }}>{label}</span>
        </span>
    );
}
